import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useTranslation } from 'react-i18next';
import { logout } from '../../context/actions';
import HeaderDropdown from './HeaderDropdown';
import LanguageSwitcher from '../common/LanguageSwitcher';
import { Menu, X, UserCircle2, LogOut, User, Bookmark, FileText, Users } from 'lucide-react';
import logo from '../../assets/logo.png';

const Header = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useTranslation();
  
  const dropdownRef = useRef(null);
  const languageDropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const handleLogout = () => {
    setIsDropdownOpen(false);
    setShowDropdown(false);
    setShowLanguageDropdown(false);
    setMobileMenuOpen(false);
    
    setTimeout(() => {
        sessionStorage.clear();
        dispatch(logout());
        navigate('/login');
    }, 0);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
        setShowLanguageDropdown(false);
      }
      
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        !event.target.closest('.mobile-menu-button')
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems = [
    {
      label: t('nav.product') || 'Product',
      to: '/'
    },
    {
      label: t('nav.pricing') || 'Pricing',
      to: '/pricing'
    },
    {
      label: t('nav.example') || 'Example',
      to: '/example'
    }
  ];

  const userMenuItems = [
    {
      icon: User,
      label: t('nav.profile') || 'Profile',
      to: '/profile'
    },
    {
      icon: FileText,
      label: t('scenarios.title') || 'My Scenarios',
      to: '/myscenarios'
    },
    {
      icon: Users, 
      label: t('nav.myTeams') || 'My Teams',
      to: '/teams' 
    },
    {
      icon: Bookmark,
      label: t('nav.mySubscription') || 'My Subscription',
      to: '/subscription'
    }
  ];

  return (
    <header className='sticky top-0 z-50 bg-white shadow-sm'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6'>
        <div className='flex justify-between items-center h-20'>
          <div className='flex-shrink-0'>
            <Link to={'/'} className='font-bold text-2xl text-indigo-600'>
              <img src={logo} alt="Logo" className="h-20 w-auto" />
            </Link>
          </div>

          <nav className='hidden md:flex items-center space-x-20'>
            {menuItems.map((item, index) => (
              <Link
                key={index}
                to={item.to}
                className='text-gray-700 hover:text-indigo-600 px-4 py-2 text-lg font-medium whitespace-nowrap'
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className='flex items-center space-x-6'>
            <div className="hidden md:block">
              <LanguageSwitcher />
            </div>

            {state.isAuthenticated && state.user ? (
              <div className="hidden md:block">
                <HeaderDropdown 
                  isOpen={isDropdownOpen}
                  onClose={setIsDropdownOpen}
                  user={state.user}
                  onLogout={handleLogout}
                  translations={t}
                />
              </div>
            ) : (
              <div className='hidden md:flex items-center space-x-6'>
                <Link to={'/login'}>
                  <button className='text-gray-700 hover:text-indigo-600 px-4 py-2 text-lg font-medium'>
                    {t('nav.login') || 'Login'}
                  </button>
                </Link>
                <Link to={'/signup'}>
                  <button className='bg-indigo-600 text-white hover:bg-indigo-700 px-6 py-3 rounded-md text-lg font-medium'>
                    {t('nav.signUp') || 'Sign Up'}
                  </button>
                </Link>
              </div>
            )}

            <button 
              className="md:hidden mobile-menu-button p-2 rounded-md text-gray-600 hover:text-indigo-600 hover:bg-gray-100 focus:outline-none"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      <div 
        ref={mobileMenuRef}
        className={`fixed inset-y-0 right-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out md:hidden ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">{t('nav.menu') || 'Menu'}</h2>
            <button 
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-md text-gray-500 hover:bg-gray-100"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="py-4 flex-grow overflow-y-auto">
            <div className="px-4 py-2">
              <LanguageSwitcher />
            </div>
            
            <nav className="mt-4">
              {menuItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.to}
                  className="block px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-indigo-600"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}

              {!state.isAuthenticated && (
                <div className="mt-4 pt-4 border-t border-gray-200 px-4 space-y-3">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <button className="w-full py-2 px-4 border border-gray-300 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50">
                      {t('nav.login') || 'Login'}
                    </button>
                  </Link>
                  <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                    <button className="w-full py-2 px-4 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-base font-medium mt-2">
                      {t('nav.signUp') || 'Sign Up'}
                    </button>
                  </Link>
                </div>
              )}
            </nav>
            
            {state.isAuthenticated && state.user && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="px-4 py-2 flex items-center">
                  <UserCircle2 className="h-9 w-9 text-indigo-600" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">{state.user.firstName}</p>
                    <p className="text-xs text-gray-500">{state.user.email}</p>
                  </div>
                </div>
                
                <div className="mt-2">
                  {userMenuItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.to}
                      className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <item.icon className="h-5 w-5 mr-3 text-gray-500" />
                      {item.label}
                    </Link>
                  ))}
                  
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="h-5 w-5 mr-3" />
                    {t('nav.signOut') || 'Sign out'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-25 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </header>
  );
};

export default Header;